import axios from "axios";
import { set } from "jodit/esm/core/helpers";
import React, { useEffect, useMemo, useState } from "react";

type UILanguage = "norwegian" | "english";
type LocalizedText = Record<UILanguage, string>;

type Channel = "voice" | "sms" | "chat";
type TelephonyProvider = "twilio" | "sip" | "other";
type ChannelFilter = "all" | Channel;

interface IVRStep {
  id: number;
  code: string;
  title: LocalizedText;
  description?: LocalizedText;
  channel: Channel;
}

interface AiLanguageTrack {
  id: string;
  language: string;
}

interface MessageConfig {
  type: "text" | "upload" | "record";
  text: string;
  fileName?: string;
  aiEnabled: boolean;
  aiLanguages: AiLanguageTrack[];
}

type MessageState = Record<string, MessageConfig>;

const UI_TEXT = {
  norwegian: {
    channels: { all: "Alle", voice: "Voice", sms: "SMS", chat: "Chat" },
    headerTitle: "Inbound IVR-oppsett",
    headerSubtitle:
      "Kortbasert, stegvis konfigurering av meldinger – hver blokk har samme funksjoner og kan aktiveres eller deaktiveres.",
    cancel: "Avbryt",
    save: "Lagre IVR-oppsett",
    telephonyTitle: "Telefoni-oppsett",
    telephonyDesc:
      "Velg hvilken leverandør som brukes for innkommende samtaler. Dette gjør det enklere å bytte mellom f.eks. Twilio og rene IP-telefoni / SIP-løsninger senere.",
    provider: "Leverandør",
    channel: "Kanal",
    channelHint: "(styrer hvilke steg som vises)",
    type: "Meldingstype",
    tts: "Tekst til tale",
    upload: "Opplastet lyd",
    record: "Rec",
    ttsLabel: "Tekst til tale",
    ttsPlaceholder: "Skriv meldingen som skal leses opp for kunden...",
    uploadLabel: "Opplastet lydfil",
    dropzoneTitle: "Slipp fil her eller klikk for å velge",
    dropzoneHint: "Støttede formater: WAV, MP3. Maks 10 MB.",
    chooseFile: "Velg fil",
    selectedFile: "Valgt fil",
    noMessage: "Dette steget er aktivt, men vil ikke spille av noen melding.",
    aiToggle: "Bruk AI-baserte språkvarianter (flere lydspor)",
    aiEmpty:
      "Legg til ett eller flere språk der systemet kan generere egne lydspor basert på hovedmeldingen.",
    selectLanguage: "Velg språk…",
    preview: "Forhåndsvis",
    play: "Spille",
    remove: "Fjern",
    addLanguage: "+ Legg til språk",
    previewTitle: "IVR-lerret (preview)",
    previewSubtitle: "Visuell oversikt over de aktive stegene, i kronologisk rekkefølge.",
    previewFooter:
      "Denne forhåndsvisningen er kun visuell og viser rekkefølgen kunden vil oppleve meldinger i IVR-løpet.",
    noSteps: "Ingen steg er aktivert ennå. Slå på minst ett steg i listen til venstre.",
    addStep: "Legg til nytt steg",
    addStepDesc:
      "Opprett et ekstra steg i IVR-løypen, med samme funksjoner som de andre kortene.",
    newStep: "Nytt steg",
    title: "Tittel",
    description: "Beskrivelse (valgfritt)",
    titlePlaceholder: "F.eks. VIP-kunder – hurtigkø",
    descriptionPlaceholder: "Kort forklaring på hva dette steget gjør...",
    createStep: "Opprett steg",
  },
  english: {
    channels: { all: "All", voice: "Voice", sms: "SMS", chat: "Chat" },
    headerTitle: "Inbound IVR setup",
    headerSubtitle:
      "Card-based, step-by-step configuration of messages where every block has the same functions and can be enabled or disabled.",
    cancel: "Cancel",
    save: "Save IVR setup",
    telephonyTitle: "Telephony setup",
    telephonyDesc:
      "Choose which provider is used for incoming calls. This makes it easier to switch later between e.g. Twilio and pure IP telephony / SIP solutions.",
    provider: "Provider",
    channel: "Channel",
    channelHint: "(controls which steps are shown)",
    type: "Message type",
    tts: "Text-to-speech",
    upload: "Uploaded audio",
    record: "Rec",
    ttsLabel: "Text to speech",
    ttsPlaceholder: "Write the message that should be read aloud to the customer...",
    uploadLabel: "Uploaded audio file",
    dropzoneTitle: "Drop a file here or click to choose",
    dropzoneHint: "Supported formats: WAV, MP3. Max 10 MB.",
    chooseFile: "Choose file",
    selectedFile: "Selected file",
    noMessage: "This step is active, but no message will be played.",
    aiToggle: "Use AI-based language variants (multiple audio tracks)",
    aiEmpty:
      "Add one or more languages where the system can generate separate audio tracks based on the main message.",
    selectLanguage: "Select language…",
    preview: "Preview",
    play: "Play",
    remove: "Remove",
    addLanguage: "+ Add language",
    previewTitle: "IVR canvas (preview)",
    previewSubtitle: "Visual overview of the active steps, in chronological order.",
    previewFooter:
      "This preview is visual only and shows the order the customer will experience messages in the IVR flow.",
    noSteps: "No steps are enabled yet. Turn on at least one step in the list on the left.",
    addStep: "Add new step",
    addStepDesc:
      "Create an additional step in the IVR flow with the same functions as the other cards.",
    newStep: "New step",
    title: "Title",
    description: "Description (optional)",
    titlePlaceholder: "E.g. VIP customers – fast queue",
    descriptionPlaceholder: "Short explanation of what this step does...",
    createStep: "Create step",
  },
} as const;

const INITIAL_STEPS: IVRStep[] = [];



const slugify = (input: string): string =>
  input.toLowerCase().trim().replace(/[^a-z0-9æøåöä\- ]/gi, "").replace(/\s+/g, "-").replace(/-+/g, "-");

const EnableToggle = ({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`relative inline-flex h-6 w-11 items-center rounded-full border text-xs transition-colors ${enabled ? "bg-amber-500 border-amber-500" : "bg-slate-200 border-slate-300"}`}
  >
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-5" : "translate-x-1"}`} />
  </button>
);

const MessageTypeSelector = ({
  value,
  disabled,
  onChange,
  ui,
}: {
  value: MessageConfig["type"];
  disabled?: boolean;
  onChange: (value: MessageConfig["type"]) => void;
  ui: (typeof UI_TEXT)[UILanguage];
}) => {
  const baseBtn = "px-3 py-1 rounded-full transition";
  return (
    <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1 text-xs">
      <button type="button" onClick={() => onChange("text")} className={`${baseBtn} ${value === "text" ? "bg-emerald-500 shadow-sm text-white" : "text-slate-500"}`} disabled={disabled}>{ui.tts}</button>
      <button type="button" onClick={() => onChange("upload")} className={`${baseBtn} ${value === "upload" ? "bg-emerald-500 shadow-sm text-white" : "text-slate-500"}`} disabled={disabled}>{ui.upload}</button>
      <button type="button" onClick={() => onChange("record")} className={`${baseBtn} ${value === "record" ? "bg-emerald-500 shadow-sm text-white" : "text-slate-500"}`} disabled={disabled}>{ui.record}</button>
    </div>
  );
};

function StepCard({
  step,
  config,
  enabled,
  language,
  ui,
  onToggle,
  onConfigChange,
  // onDragStart,
  // onDragOverCard,
  // onDropCard,
  // isDropTarget,
}: {
  step: IVRStep;
  config: MessageConfig;
  enabled: boolean;
  language: UILanguage;
  ui: (typeof UI_TEXT)[UILanguage];
  onToggle: () => void;
  onConfigChange: (patch: Partial<MessageConfig>) => void;
  // onDragStart?: (stepId: number) => void;
  // onDragOverCard?: (stepId: number, event: React.DragEvent) => void;
  // onDropCard?: (stepId: number, event: React.DragEvent) => void;
  // isDropTarget?: boolean;
}) {
  // Local state for the preview of a newly selected (not yet uploaded) file
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  // Recording States
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);

  // --- Text to Speech ---
  const [isSpeaking, setIsSpeaking] = useState(false);
  const playTTS = () => {
    const textToSpeak = config?.text;
    if (!textToSpeak) return;

    // 1. Cancel anything already playing
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(textToSpeak);

    const langMap: Record<string, string> = {
      english: "en-US", norwegian: "no-NO", danish: "da-DK",
      german: "de-DE", french: "fr-FR", dutch: "nl-NL",
      swedish: "sv-SE", spanish: "es-ES"
    };

    utterance.lang = langMap[language] || "en-US";

    // 2. Handle State Changes
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  };

  const stopTTS = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  // --- Recording Logic ---
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        // Naming it .webm so PHP/React knows it's a recording
        const fileName = `rec_${Date.now()}.webm`;
        const file = new File([blob], fileName, { type: "audio/webm" });

        const objectUrl = URL.createObjectURL(file);
        setLocalPreview(objectUrl);

        // Explicitly passing 'none' as type to ensure logic picks 'record' key
        onConfigChange({ fileName: fileName, type: "none" }, file, language);
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (err) {
      console.error("Microphone access denied:", err);
      alert("Microphone access denied.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const handleFileChange = (file?: File | null) => {
    if (file) {
      // Create a temporary blob URL so the <audio> tag can play the local file
      const objectUrl = URL.createObjectURL(file);
      setLocalPreview(objectUrl);
      onConfigChange({ fileName: file.name }, file, language);
    } else {
      setLocalPreview(null);
      onConfigChange({ fileName: undefined }, undefined, language);
    }
  };

  // Determine the audio source: 
  // 1. Priority to the local preview (unsaved)
  // 2. Fallback to the server path (saved)
  const audioSrc = localPreview || (config?.fileName ? config?.fileName : null);
  // const handleDrop: React.DragEventHandler<HTMLDivElement> = (event) => {
  //   event.preventDefault();
  //   if (!enabled) return;
  //   const file = event.dataTransfer.files?.[0];
  //   if (file) handleFileChange(file);
  // };
  // const handleDragOver: React.DragEventHandler<HTMLDivElement> = (event) => event.preventDefault();
  const channelLabel = ui.channels[step.channel];

  useEffect(() => {
    // Cleanup function: runs when the component is destroyed
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  return (
    <article
      // draggable
      // onDragStart={() => onDragStart?.(step.id)}
      // onDragOver={(event) => onDragOverCard?.(step.id, event)}
      // onDrop={(event) => onDropCard?.(step.id, event)}
      className={`rounded-2xl border shadow-sm bg-white overflow-hidden transition-all min-h-[316px] ${enabled ? "border-amber-400" : "border-slate-200 opacity-80"} ${/*isDropTarget ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-50" : */""}`}
    >
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b bg-slate-50 cursor-move">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-500 text-xs font-semibold text-white">{step.id}</span>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold leading-tight">{step.title[language]}</h2>
              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-[10px] font-medium uppercase tracking-wide text-slate-600">{channelLabel}</span>
            </div>
            {step.description && <p className="text-xs text-slate-500 mt-0.5">{step.description[language]}</p>}
          </div>
        </div>
        {/* <EnableToggle enabled={enabled} onToggle={onToggle} /> */}
      </header>

      <div className="px-4 py-3 space-y-3 text-xs md:text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{ui.type}</span>
          <MessageTypeSelector value={config?.type} disabled={!enabled} onChange={(type) => onConfigChange({ type })} ui={ui} />
        </div>

        {config?.type === "text" && (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-slate-600">{ui.ttsLabel}</label>

              {!isSpeaking ? (
                <button
                  onClick={playTTS}
                  disabled={!config?.text || !enabled}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 text-amber-600 text-[10px] font-bold uppercase hover:bg-amber-100 disabled:opacity-50 transition-colors border border-amber-200"
                >
                  <svg viewBox="0 0 24 24" className="h-3 w-3 fill-current"><path d="M8 5v14l11-7z" /></svg>
                  {ui.play || "Play"}
                </button>
              ) : (
                <button
                  onClick={stopTTS}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-rose-50 text-rose-600 text-[10px] font-bold uppercase hover:bg-rose-100 transition-colors border border-rose-200"
                >
                  <svg viewBox="0 0 24 24" className="h-3 w-3 fill-current"><path d="M6 6h12v12H6z" /></svg>
                  Stop
                </button>
              )}
            </div>

            <textarea
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs focus:ring-2 focus:ring-amber-400 focus:outline-none"
              rows={3}
              placeholder={ui.ttsPlaceholder}
              value={config?.text}
              onChange={(e) => {
                if (isSpeaking) stopTTS(); // Stop speaking if the user starts typing
                onConfigChange({ text: e.target.value });
              }}
              disabled={!enabled}
            />
          </div>
        )}

        {config?.type === "upload" && (
          <div className="space-y-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600">{ui.uploadLabel}</label>
              <div className="flex flex-col gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-[11px] cursor-pointer hover:border-amber-400 hover:bg-amber-50/50">
                <span className="font-medium">{ui.dropzoneTitle}</span>
                <label className="mt-1 inline-flex w-fit items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1 text-[11px] shadow-sm hover:bg-slate-50 cursor-pointer">
                  <span>{ui.chooseFile}</span>
                  <input
                    type="file"
                    className="hidden"
                    accept="audio/*"
                    disabled={!enabled}
                    onChange={(e) => handleFileChange(e.target.files?.[0])}
                  />
                </label>
                {config?.fileName && (
                  <span className="text-[11px] text-emerald-600 font-medium">
                    {ui.selectedFile}: {config.fileName}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* --- TYPE: RECORD (NONE) --- */}
        {config?.type === "record" && (
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
              {!isRecording ? (
                <button
                  onClick={startRecording}
                  disabled={!enabled}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-rose-500 text-white text-xs font-medium hover:bg-rose-600 disabled:opacity-50"
                >
                  <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                  Start Recording
                </button>
              ) : (
                <button
                  onClick={stopRecording}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 text-white text-xs font-medium hover:bg-slate-900"
                >
                  <span className="h-2 w-2 bg-white" />
                  Stop Recording
                </button>
              )}
            </div>
            {isRecording && <p className="text-center text-[10px] text-rose-500 animate-pulse font-medium">Recording in progress...</p>}
          </div>
        )}

        {/* Global Audio Preview for Uploaded or Recorded files */}
        {audioSrc && (
          <div className="pt-2 border-t border-slate-100">
            <audio key={audioSrc} controls className="w-full h-8 scale-95 origin-left">
              <source src={audioSrc} />
            </audio>
          </div>
        )}

        {/* <div className="mt-1 space-y-2 border-t pt-3 border-slate-100">
          <label className="inline-flex items-center gap-2 text-[11px] font-medium text-slate-600">
            <input type="checkbox" className="h-3.5 w-3.5 rounded border-slate-300 text-amber-500 focus:ring-amber-400" checked={config?.aiEnabled} onChange={() => onConfigChange({ aiEnabled: !config?.aiEnabled })} disabled={!enabled} />
            {ui.aiToggle}
          </label>
          {config?.aiEnabled && (
            <div className="space-y-2 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
              {config?.aiLanguages.length === 0 && <p className="text-[11px] text-slate-500">{ui.aiEmpty}</p>}
              {config?.aiLanguages.map((track) => (
                <div key={track.id} className="flex items-center gap-2">
                  <select className="flex-1 rounded-lg border border-slate-300 bg-white px-2 py-1 text-[11px] focus:outline-none focus:ring-2 focus:ring-amber-400" value={track.language} onChange={(e) => onConfigChange({ aiLanguages: config?.aiLanguages.map((t) => (t.id === track.id ? { ...t, language: e.target.value } : t)) })}>
                    <option value="">{ui.selectLanguage}</option>
                    <option value="nb-NO">Norsk (bokmål)</option>
                    <option value="nn-NO">Norsk (nynorsk)</option>
                    <option value="sv-SE">Svensk</option>
                    <option value="da-DK">Dansk</option>
                    <option value="en-GB">English (UK)</option>
                    <option value="en-US">English (US)</option>
                  </select>
                  <button type="button" className="px-2 py-1 rounded-lg border border-slate-200 text-[11px] bg-white hover:bg-slate-100">{ui.preview}</button>
                  <button type="button" className="px-2 py-1 rounded-lg border border-rose-200 text-[11px] text-rose-600 bg-white hover:bg-rose-50" onClick={() => onConfigChange({ aiLanguages: config?.aiLanguages.filter((t) => t.id !== track.id) })}>{ui.remove}</button>
                </div>
              ))}
              <button type="button" className="mt-1 inline-flex items-center gap-1 rounded-lg border border-dashed border-amber-400 px-2.5 py-1 text-[11px] font-medium text-amber-600 hover:bg-amber-50" onClick={() => onConfigChange({ aiLanguages: [...config?.aiLanguages, { id: `${Date.now()}-${Math.random().toString(16).slice(2)}`, language: "" }] })} disabled={!enabled}>{ui.addLanguage}</button>
            </div>
          )}
        </div> */}
      </div>
    </article>
  );
}

function CanvasPreview({
  steps,
  enabledSteps,
  messages,
  language,
  ui,
}: {
  steps: IVRStep[];
  enabledSteps: Record<string, boolean>;
  messages: MessageState;
  language: UILanguage;
  ui: (typeof UI_TEXT)[UILanguage];
}) {
  const hasAny = Object.values(enabledSteps).some(Boolean);
  return (
    <aside className="sticky top-4 rounded-2xl border border-slate-200 bg-white shadow-sm p-4 space-y-3">
      <header className="flex items-center justify-between gap-3 pb-2 border-b">
        <div>
          <h2 className="text-sm font-semibold">{ui.previewTitle}</h2>
          <p className="text-xs text-slate-500">{ui.previewSubtitle}</p>
        </div>
      </header>
      <div className="relative mt-2">
        <div className="absolute left-4 top-0 bottom-0 w-px bg-gradient-to-b from-amber-400/70 via-slate-200 to-slate-200 pointer-events-none" />
        <ol className="space-y-3 pl-8 text-xs">
          {steps.map((step) => {
            const isEnabled = enabledSteps[step.code];
            const config = messages[step.code];
            if (!isEnabled) return null;
            return (
              <li key={step.code} className="relative">
                <div className="absolute -left-[23px] top-1 h-4 w-4 rounded-full border-2 border-amber-400 bg-white flex items-center justify-center text-[10px] font-semibold text-amber-500">{step.id}</div>
                <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-3 py-2 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[11px] md:text-xs">{step.title[language]}</span>
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-[9px] font-medium uppercase tracking-wide text-amber-700">{ui.channels[step.channel]}</span>
                    </div>
                    <span className="text-[10px] uppercase tracking-wide text-amber-600 font-semibold">{config?.type === "text" ? "TEXT" : config?.type === "upload" ? ui.upload : ui.record}</span>
                  </div>
                  {config?.text && config?.type === "text" && <p className="mt-1 text-[11px] text-slate-600 line-clamp-2">{config?.text}</p>}
                </div>
              </li>
            );
          })}
          {!hasAny && <li className="text-[11px] text-slate-400 italic">{ui.noSteps}</li>}
        </ol>
      </div>
      <footer className="pt-2 border-t mt-1"><p className="text-[11px] text-slate-500">{ui.previewFooter}</p></footer>
    </aside>
  );
}

function AddStepCard({
  onCreate,
  ui,
}: {
  onCreate: (data: { title: string; description?: string }) => void;
  ui: (typeof UI_TEXT)[UILanguage];
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-amber-300 bg-amber-50/40 px-4 py-6 text-xs text-amber-700 hover:bg-amber-50 hover:border-amber-400 shadow-sm">
        <span className="mb-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-white text-xl">+</span>
        <span className="font-medium">{ui.addStep}</span>
        <span className="mt-1 text-[11px] text-amber-700/80 text-center">{ui.addStepDesc}</span>
      </button>
    );
  }

  return (
    <form onSubmit={(event) => {
      event.preventDefault();
      const trimmedTitle = title.trim();
      const trimmedDescription = description.trim();
      if (!trimmedTitle) return;
      onCreate({ title: trimmedTitle, description: trimmedDescription || undefined });
      setTitle("");
      setDescription("");
      setOpen(false);
    }} className="rounded-2xl border border-amber-300 bg-white px-4 py-4 shadow-sm space-y-3 text-xs md:text-sm">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-800">{ui.newStep}</h2>
        <button type="button" onClick={() => setOpen(false)} className="text-[11px] text-slate-500 hover:text-slate-700">{ui.cancel}</button>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-medium text-slate-600">{ui.title}</label>
        <input className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder={ui.titlePlaceholder} value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-medium text-slate-600">{ui.description}</label>
        <textarea className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400" rows={2} placeholder={ui.descriptionPlaceholder} value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button type="submit" className="px-3 py-1.5 rounded-xl bg-amber-500 text-white text-xs font-medium hover:bg-amber-600">{ui.createStep}</button>
      </div>
    </form>
  );
}

type AdministerTelefoniProps = {
  client: any;
  currentUser: any;
  baseUrl: string;
};

export default function AdministerTelefoni({ client, currentUser, baseUrl }: AdministerTelefoniProps) {

  const [steps, setSteps] = useState<IVRStep[]>(INITIAL_STEPS);
  const [metaValue, setMetaValue] = useState([]);
  const getMetaValue = async () => {
    if (!client || !currentUser?.token || !client?.uid) return;
    try {
      const response = await axios.get(`${baseUrl}/getIvrMessages`, { params: { token: currentUser.token, client_uid: client.uid } });
      if (response.data.status === "OK") {
        setMetaValue(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching IVR steps:", error);
    }
  }

  useEffect(() => {
    getMetaValue();
  }, [client, currentUser, baseUrl]);
  const [enabledSteps, setEnabledSteps] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!metaValue || metaValue.length === 0) return;

    const initial: Record<string, boolean> = {};

    metaValue.forEach((s: any) => {
      initial[s.code] = s.enabled;
    });

    setEnabledSteps(initial);
  }, [metaValue]);

  const [language, setLanguage] = useState<UILanguage>("norwegian");
  const [fileBinaries, setFileBinaries] = useState<Record<string, File>>({});
  const defaultMessageState: MessageState = useMemo(() => {
    return metaValue.reduce((acc, step) => {
      acc[step.code] = {
        type: step.config.type[language],
        text: step.config.text[language],
        fileName: step.config.fileName[language],
        aiEnabled: false,
        aiLanguages: [],
      };
      return acc;
    }, {} as MessageState);
  }, [metaValue, language]);

  const [messages, setMessages] = useState<MessageState>({});

  useEffect(() => {
    if (Object.keys(defaultMessageState).length === 0) return;
    setMessages(defaultMessageState);
  }, [defaultMessageState]);
  const [provider, setProvider] = useState<TelephonyProvider>("twilio");
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>("all");


  const ui = UI_TEXT[language];
  const filteredSteps = useMemo(() => steps.filter((step) => (channelFilter === "all" ? true : step.channel === channelFilter)), [steps, channelFilter]);

  const handleCreateStep = (data: { title: string; description?: string }) => {
    setSteps((prev) => {
      const maxId = prev.reduce((max, step) => Math.max(max, step.id), 0);
      const newId = maxId + 1;
      const existingCodes = new Set(prev.map((s) => s.code));
      let baseCode = slugify(data.title || `custom-step-${newId}`) || `step-${newId}`;
      let code = baseCode;
      let counter = 2;
      while (existingCodes.has(code)) code = `${baseCode}-${counter++}`;
      const newStep: IVRStep = {
        id: newId,
        code,
        title: { norwegian: data.title, english: data.title },
        description: data.description ? { norwegian: data.description, english: data.description } : undefined,
        channel: channelFilter === "all" ? "voice" : channelFilter,
      };
      setMessages((prevMessages) => ({ ...prevMessages, [code]: { type: "record", text: "", fileName: undefined, aiEnabled: false, aiLanguages: [] } }));
      setEnabledSteps((prevEnabled) => ({ ...prevEnabled, [code]: true }));
      return [...prev, newStep];
    });
  };

  const updateMetaValue = (code: string, patch: any) => {
    setMetaValue((prev) =>
      prev.map((step) => {
        if (step.code === code) {
          return {
            ...step,
            // If the patch contains config changes, merge them into the nested language object
            config: patch.config
              ? { ...step.config, ...patch.config }
              : step.config,
            // If the patch contains top-level changes like 'enabled'
            ...patch.topLevel
          };
        }
        return step;
      })
    );
  };

  const handleSaveIvrFlow = async () => {
    // Check if there is anything to save
    if (!metaValue || metaValue.length === 0) return;

    const formData = new FormData();

    // 1. Add Auth and Metadata
    formData.append("token", currentUser.token);
    formData.append("client_uid", client.uid);

    /**
     * 2. The JSON structure (metaValue)
     * This now includes config.fileName for uploads 
     * AND config.record for recordings.
     */
    formData.append("metaValue", JSON.stringify(metaValue));

    /**
     * 3. Add the actual binary files (Uploads OR Recordings)
     * The fileBinaries state stores them as:
     * { "step-code_english": FileObject }
     */
    Object.entries(fileBinaries).forEach(([binaryKey, file]) => {
      // binaryKey is "code_language" (e.g., "welcome_msg_norwegian")
      // This allows PHP to map the file to the correct step and language
      formData.append(binaryKey, file);
    });

    try {
      // Show a loading state here if you have one
      const response = await axios.post(`${baseUrl}/updateIvrMessages`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status === "OK") {
        // SUCCESS CLEANUP
        // 1. Clear the staged binary files since they are now on the server
        setFileBinaries({});

        // 2. Optional: If you use local preview URLs (URL.createObjectURL), 
        // you should ideally revoke them here to free up memory, 
        // though browser cleanup usually handles this.

        alert("Saved successfully!");

        // 3. Optional: Refresh the metaValue from the server to get the new base_url() paths
        // getMetaValue(); 
      } else {
        alert("Error: " + (response.data.data || "Unknown error"));
      }
    } catch (error) {
      console.error("Upload failed", error);
      alert("Server error during upload. Please try again.");
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 flex flex-col gap-6 text-slate-900">
      <header className="flex flex-col gap-4 md:gap-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{ui.headerTitle}</h1>
            <p className="text-sm text-slate-500 max-w-xl">{ui.headerSubtitle}</p>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1 text-xs shadow-sm">
              <button type="button" onClick={() => setLanguage("norwegian")} className={`px-3 py-1 rounded-full ${language === "norwegian" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>NO</button>
              <button type="button" onClick={() => setLanguage("english")} className={`px-3 py-1 rounded-full ${language === "english" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>EN</button>
            </div>
            <button className="px-4 py-2 rounded-xl text-sm border border-slate-300 bg-white shadow-sm hover:bg-slate-100">{ui.cancel}</button>
            <button onClick={handleSaveIvrFlow} className="px-4 py-2 rounded-xl text-sm bg-amber-500 text-white shadow hover:bg-amber-600">{ui.save}</button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">{ui.telephonyTitle}</span>
            <p className="text-xs text-slate-500 max-w-2xl">{ui.telephonyDesc}</p>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-600">{ui.provider}</label>
            <select className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400" value={provider} onChange={(e) => setProvider(e.target.value as TelephonyProvider)}>
              <option value="twilio">Twilio</option>
              <option value="sip">IP-telefoni / SIP</option>
              <option value="other">Other / Annen</option>
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="font-medium uppercase tracking-wide">{ui.channel}</span>
            <span className="text-slate-400">{ui.channelHint}</span>
          </div>
          <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1 text-xs shadow-sm">
            {(["all", "voice", "sms", "chat"] as const).map((channel) => (
              <button key={channel} type="button" onClick={() => setChannelFilter(channel)} className={`px-3 py-1 rounded-full ${channelFilter === channel ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                {ui.channels[channel]}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="grid grid-cols-1 gap-6 items-start">
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          {metaValue.map((step) => (
            <StepCard
              key={step.code}
              step={step}
              config={messages[step.code]}
              enabled={enabledSteps[step.code]}
              language={language}
              ui={ui}
              onToggle={() => {
                const newStatus = !enabledSteps[step.code];
                // Update local UI state
                setEnabledSteps((prev) => ({ ...prev, [step.code]: newStatus }));
                // Sync back to metaValue
                updateMetaValue(step.code, { topLevel: { enabled: newStatus } });
              }}
              onConfigChange={(patch, file, fileLanguage) => {
                // 1. Update the UI state (Local state for the cards)
                setMessages((prev) => ({
                  ...prev,
                  [step.code]: { ...prev[step.code], ...patch },
                }));

                // 2. Update metaValue (The JSON structure for PHP)
                setMetaValue((prev) =>
                  prev.map((s) => {
                    if (s.code === step.code) {
                      // Create a deep copy of the config to avoid direct mutation
                      const newConfig = {
                        ...s.config,
                        type: { ...s.config.type },
                        text: { ...s.config.text },
                        fileName: { ...s.config.fileName },
                        record: { ...(s.config.record || {}) }
                      };

                      // --- UPDATE SELECTED TYPE ---
                      // This sets metaValue->config->type->[language] to 'text', 'upload', or 'record'
                      if (patch.type) {
                        newConfig.type[language] = patch.type;
                      }

                      // Update text if provided
                      if (patch.text !== undefined) {
                        newConfig.text[language] = patch.text;
                      }

                      // --- SEPARATE STORAGE LOGIC ---
                      if (patch.fileName !== undefined) {
                        // Check if the current language mode is 'record' (formerly 'none')
                        const currentMode = patch.type || s.config.type[language];

                        if (currentMode === "record" || currentMode === "none") {
                          // Store filename in the record object
                          newConfig.record[language] = patch.fileName;
                        } else {
                          // Store filename in the standard upload object
                          newConfig.fileName[language] = patch.fileName;
                        }
                      }

                      return { ...s, config: newConfig };
                    }
                    return s;
                  })
                );

                // 3. Store the Binary File for the FormData upload
                if (file && fileLanguage) {
                  const binaryKey = `${step.code}_${fileLanguage}`;
                  setFileBinaries((prev) => ({ ...prev, [binaryKey]: file }));
                }
              }}
            // onDragStart={setDraggingId}
            // onDragOverCard={(stepId, event) => {
            //   event.preventDefault();
            //   if (!draggingId || draggingId === stepId) return;
            //   setDropTargetId(stepId);
            // }}
            // onDropCard={(targetId, event) => {
            //   event.preventDefault();
            //   if (!draggingId || draggingId === targetId) return;
            //   setSteps((prev) => {
            //     const next = [...prev];
            //     const fromIndex = next.findIndex((s) => s.id === draggingId);
            //     const toIndex = next.findIndex((s) => s.id === targetId);
            //     if (fromIndex === -1 || toIndex === -1) return prev;
            //     const moved = next.splice(fromIndex, 1)[0];
            //     next.splice(toIndex, 0, moved);
            //     return next;
            //   });
            //   setDraggingId(null);
            //   setDropTargetId(null);
            // }}
            // isDropTarget={dropTargetId === step.id && draggingId !== step.id}
            />
          ))}
          {/* <AddStepCard onCreate={handleCreateStep} ui={ui} /> */}
        </section>
        {/* <CanvasPreview steps={filteredSteps} enabledSteps={enabledSteps} messages={messages} language={language} ui={ui} /> */}
      </main>
    </div>
  );
}
